import type { EntityItem } from '@epilot/customer-portal-client'

interface GroupedMockData {
  groups: GroupedResult[]
  total: number
}

interface GroupedResult {
  count: number
  group: string
  results: EntityItem[]
}

export const extractUniqueFieldValues = (
  entities: EntityItem[],
  fieldPath: string
): string[] => {
  const values = new Set<string>()

  entities.forEach((entity) => {
    const value = getNestedValue(entity, fieldPath)

    if (value && typeof value === 'string') {
      values.add(value)
    }
  })

  return Array.from(values).sort()
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

export const findBestGroupingField = (
  entities: EntityItem[],
  schema?: string
): string => {
  const preferredFields = {
    contract: ['branch', 'sector', 'status', 'type'],
    file: ['category', 'mime_type', 'type'],
    meter: ['sector', 'status', 'type'],
    opportunity: ['stage', 'status', 'type'],
    order: ['source_type', 'status', 'type']
  }

  const fieldsToTry = schema ? preferredFields[schema] || [] : []

  fieldsToTry.push('_schema', 'branch', 'sector', 'status', 'type')

  for (const field of fieldsToTry) {
    const uniqueValues = extractUniqueFieldValues(entities, field)

    if (uniqueValues.length > 1 && uniqueValues.length <= 5) {
      return field
    }
  }

  if (entities.length > 0) {
    if (entities[0]._title) return '_title'
    if (entities[0]._id) return '_id'
  }

  return 'default'
}

export const createGroupedMockData = (
  entities: EntityItem[],
  schema?: string
): GroupedMockData => {
  if (!entities || entities.length === 0) {
    return { groups: [], total: 0 }
  }

  const field = findBestGroupingField(entities, schema)
  const uniqueValues = extractUniqueFieldValues(entities, field)

  if (uniqueValues.length <= 1) {
    return createArtificialGroups(entities)
  }

  const groups: GroupedResult[] = uniqueValues.map((value) => {
    const filtered = entities.filter(
      (entity) => getNestedValue(entity, field) === value
    )

    return {
      group: value,
      count: filtered.length,
      results: filtered
    }
  })

  return {
    groups: groups.filter((group) => group.count > 0),
    total: entities.length
  }
}

const createArtificialGroups = (entities: EntityItem[]): GroupedMockData => {
  const midPoint = Math.ceil(entities.length / 2)

  return {
    groups: [
      {
        group: 'group_a',
        count: midPoint,
        results: entities.slice(0, midPoint)
      },
      {
        group: 'group_b',
        count: entities.length - midPoint,
        results: entities.slice(midPoint)
      }
    ].filter((group) => group.count > 0),
    total: entities.length
  }
}
